import React, {
 useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import { Schema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Spinner from '../spinner/Spinner';
import { FormInputHidden } from '..';
import useStyles from './styles';
import { ItemFormContext, useItemFormContext } from './ReactItemFormContext';
import CloseTabReactItemFormDetector from './CloseTabReactItemFormDetector';
import FormPreventRedirectModal from './FormPreventRedirectModal';
import ItemFormControlsReact from './ItemFormControlsReact';
import { CONTROLS_CONTAINER_HEIGHT } from './ItemFormControls';

type Props = {
  creatingMode?: boolean;
  loading: boolean;
  children: React.ReactNode;
  initialValues: any | null;
  validationSchema: Schema<unknown>;
  onSubmit: (values: any) => void;
  titleSubmit?: string;
  hideActions?: boolean;
  editPermitted?: boolean;
  preventCancelChanges?: boolean;
};

const ItemFormReact = ({
  children,
  initialValues,
  loading,
  validationSchema,
  onSubmit,
  creatingMode,
  titleSubmit,
  hideActions,
  preventCancelChanges = false,
}: Props) => {
  const classes = useStyles();
  const [isBottom, setIsBottom] = useState(false);
  const containerRef = useRef(null);

  const form = useForm({
    defaultValues: initialValues || undefined,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    shouldUnregister: false,
  });

  const { reset, handleSubmit, formState: { isDirty } } = form;

  useMemo(() => {
    reset(initialValues || undefined);
  }, [reset, initialValues]);

  const checkFormOutOfView = useCallback(() => {
    const el = containerRef.current;
    if (!loading && el) {
      // @ts-ignore
      const boundObj = el.getBoundingClientRect();

      if (boundObj.bottom + CONTROLS_CONTAINER_HEIGHT > (window.innerHeight || document.documentElement.clientHeight)) {
        setIsBottom(false);
      } else {
        setIsBottom(true);
      }
    }
  }, [containerRef, loading, setIsBottom]);

  const checkScrollEnd = useCallback(() => {
    const docEl = document.documentElement;
    setIsBottom((docEl.scrollHeight - docEl.scrollTop < docEl.clientHeight + 100 - 2));
    if (docEl.scrollHeight === docEl.clientHeight) setIsBottom(true);
    if (docEl.scrollHeight - docEl.clientHeight < 100) {
      setIsBottom(true);
    }
    if (docEl.scrollTop === 0) setIsBottom(false);

    checkFormOutOfView();
  }, [setIsBottom, checkFormOutOfView]);

  useEffect(() => {
    checkScrollEnd();
  }, [checkScrollEnd]);

  // check if form go out of bottom
  useEffect(() => {
    checkFormOutOfView();
  }, [checkFormOutOfView]);

  useEffect(() => {
    window.addEventListener('scroll', checkScrollEnd);
    window.addEventListener('resize', checkScrollEnd);
    return () => {
      window.removeEventListener('scroll', checkScrollEnd);
      window.removeEventListener('resize', checkScrollEnd);
    };
  }, [setIsBottom, isBottom, checkScrollEnd]);

  const submitHandler = useCallback((data: any) => {
    onSubmit(data);
    reset(
      data,
      {
        errors: false,
        dirtyFields: false,
        isDirty: false,
        isSubmitted: false,
        touched: false,
        isValid: false,
        submitCount: false,
      },
    );
  }, [onSubmit, reset]);

  const submit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler]);

  const itemFormContext = useItemFormContext({ submit });

  if (loading) {
    return (
      <Grid container className={classes.spinnerWrapper}>
        <Spinner />
      </Grid>
    );
  }

  if (initialValues === null) {
    return null;
  }
  const itemId = initialValues.id;

  return (
    <FormProvider {...form}>
      {isDirty && preventCancelChanges && <FormPreventRedirectModal preventCancelChanges />}

      <Grid ref={containerRef} container className={classes.root}>
        <form
          onSubmit={submit}
          className={classes.form}
        >
          <ItemFormContext.Provider value={itemFormContext}>
            <CloseTabReactItemFormDetector />
            {itemId && <FormInputHidden name="id" />}
            {children}
            {((isDirty || creatingMode) && !loading && !hideActions) && (
              <ItemFormControlsReact
                submitButtonText={titleSubmit || (creatingMode ? 'Create' : 'Save')}
                preventCancelChanges={preventCancelChanges}
                dirty={isDirty}
                loading={loading}
                position={isBottom ? 'relative' : 'fixed'}
              />
            )}
          </ItemFormContext.Provider>
        </form>
      </Grid>
    </FormProvider>
  );
};

export default ItemFormReact;
